"use client";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  EcommerceTermsProvider,
  useEcommerceTerms,
} from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";

declare const wvcClient: {
  getCurrentWPTermQueryParams: () => Record<string, unknown>;
};

function ShopBreadcrumbsConsumer() {
  const { links, loading, error } = useShopBreadcrumbs();

  if (loading || error || links.length === 0) return null;

  const lastIndex = links.length - 1;

  return (
    <Breadcrumb>
      <BreadcrumbList className="text-muted-foreground tracking-wider uppercase text-xs sm:text-sm">
        {links.map((link, i) => (
          <span data-index={i} key={link.href} className="inline-flex items-center gap-1.5 sm:gap-2.5">
            <BreadcrumbItem>
              {i === lastIndex ? (
                <BreadcrumbPage className="font-default text-foreground">
                  {link.title}
                </BreadcrumbPage>
              ) : (
                <BreadcrumbLink
                  href={link.href}
                  className="font-default hover:text-primary transition-colors"
                >
                  {link.title}
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {i < lastIndex && <BreadcrumbSeparator />}
          </span>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function ArchiveTitleConsumer() {
  const { terms, loading } = useEcommerceTerms();

  if (loading) {
    return (
      <div data-section-id="5437" className="mt-8 sm:mt-10">
        <div className="h-12 w-72 max-w-full bg-muted/40 animate-pulse rounded-lg" />
        <div className="mt-6 h-4 w-96 max-w-full bg-muted/40 animate-pulse rounded-lg" />
      </div>
    );
  }

  const term = terms[0];
  if (!term) return null;

  return (
    <div className="mt-8 sm:mt-10 max-w-3xl">
      <h1 className="font-default font-medium text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-foreground tracking-tight leading-[1.05]">
        {term.name}
      </h1>
      {term.description && (
        <p
          className="font-default mt-6 sm:mt-8 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl"
          dangerouslySetInnerHTML={{ __html: term.description }}
        />
      )}
      <div className="mt-8 sm:mt-10 h-px w-24 bg-primary" />
    </div>
  );
}

export default function () {
  const current_wp_term_query_params = wvcClient.getCurrentWPTermQueryParams();
  const wp_term_query = new WP_Term_Query(current_wp_term_query_params);

  return (
    <section
      id="archive-title-section"
      className="bg-background text-foreground w-full pt-28 sm:pt-32 md:pt-36 pb-12 sm:pb-16 md:pb-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <ShopBreadcrumbsProvider>
          <ShopBreadcrumbsConsumer />
        </ShopBreadcrumbsProvider>

        <EcommerceTermsProvider wp_term_query={wp_term_query}>
          <ArchiveTitleConsumer />
        </EcommerceTermsProvider>
      </div>
    </section>
  );
}